import { AuditActionOption } from '../../components/services/auditService';

export interface ActionCategory {
  name: string;
  /** Sorted by customer-facing name. */
  actions: AuditActionOption[];
}

// Action codes are "<Area>.<Verb>" (Defatom.Shared AuditActionEnum, "Area_Verb"). Each area belongs to one category;
// the categories are listed in this order. An area not listed here (a newer service's action) goes under "Other".
const CATEGORY_AREAS: [string, string[]][] = [
  ['Security', ['Security']],
  ['Users and access', ['User', 'Role', 'ApiScope', 'AppRegistration']],
  ['Tenants', ['Tenant', 'TenantSetting', 'TenantSystemSettings', 'TenantData']],
  ['Groups, devices and scripts', ['TenantGroup', 'TenantAgent', 'TenantScript']],
  ['Remediation', ['RemediationPlan', 'RemediationTask']],
  ['Software deployment', ['SoftwareDeployment', 'SoftwarePolicy']],
  ['Group policies', ['GroupPolicyAutomation']],
  ['Software catalog', ['InstallerManifest', 'PackageMapping', 'WingetPackage', 'WingetManifest']],
  ['Vulnerabilities', ['Cve']],
  ['Platform maintenance', ['Cache']],
];

export const OTHER_CATEGORY = 'Other';

const CATEGORY_BY_AREA: Record<string, string> = Object.fromEntries(
  CATEGORY_AREAS.flatMap(([category, areas]) => areas.map((area) => [area, category])),
);

/** The category an action code belongs to, e.g. "RemediationPlan.Retry" -> "Remediation". */
export const actionCategoryOf = (code: string): string => CATEGORY_BY_AREA[code.split('.')[0]] ?? OTHER_CATEGORY;

/** The actions grouped by category, in category order, without empty categories. */
export const categorizeActions = (options: AuditActionOption[]): ActionCategory[] => {
  const byCategory = new Map<string, AuditActionOption[]>();
  options.forEach((option) => {
    const category = actionCategoryOf(option.action);
    byCategory.set(category, [...(byCategory.get(category) ?? []), option]);
  });
  return [...CATEGORY_AREAS.map(([category]) => category), OTHER_CATEGORY]
    .filter((category) => byCategory.has(category))
    .map((category) => ({
      name: category,
      actions: [...byCategory.get(category)!].sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })),
    }));
};
